import * as React from 'react';
import { StyleSheet } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import {
  PrimaryLinksComponentProps,
  PrimaryLinksDataProps,
  PrimaryLinksThemeProps,
} from '../../components/navigation/header/primaryHeader/PrimaryLinks';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { useTheme } from '../../../theming/useTheme';
import { generateLinkStyleFromTheme, TextLinkComponent } from '../../partials/link/TextLink';
import { HeaderLinksDataProps } from '../../components/navigation/header/Header';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { Span, semanticCSS } from '../../../typography/semanticTags';
import { FontSizesStyles, getStyleBySize } from '../../../typography/semanticTags/semanticTags.utils';

const additionalStyle = {
  [MEDIA_BREAKPOINTS.LARGE]: {
    display: 'block',
  },
  [MEDIA_BREAKPOINTS.MEDIUM]: {
    display: 'none',
  },
  [MEDIA_BREAKPOINTS.SMALL]: {
    display: 'none',
  },
};

const radiusBorder = 4;

const createStyle = (isActive: boolean = false, activeBorderBottom: string) => {
  return StyleSheet.create({
    link: {
      height: '100%',
      width: '100%',
      display: 'inline-flex',
      alignItems: 'center',
      position: 'relative',
      fontWeight: isActive ? 600 : 400,
      ':before': isActive ? {
        height: `${radiusBorder}px`,
        bottom: 0,
        left: 0,
        position: 'absolute',
        right: 0,
        content: "''",
        backgroundColor: activeBorderBottom,
      } : {},
    },
  });
};

const createLink = (idleColor: string, activeBorderBottom: string, hoverStateColor: string, activeStateColor: string, pagePath: string, fontSizesStyles: FontSizesStyles) => (link: HeaderLinksDataProps, index: number) => {
  const { href, text } = link;
  const isActiveLink = href === pagePath;
  const fontsSizesForActive = { fontSizeSmall: TYPOGRAPHY_SIZE.BIGGER, fontSizeMedium: TYPOGRAPHY_SIZE.BIGGER, fontSizeLarge: TYPOGRAPHY_SIZE.BIGGER };
  const fontsSizesForNotActive = { fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL, fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL, fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL };
  const contentStyle = getStyleBySize(fontSizesStyles, isActiveLink ? fontsSizesForActive : fontsSizesForNotActive);
  const linkStyle = generateLinkStyleFromTheme({ idleColor, activeStateColor, hoverStateColor }, additionalStyle);
  const style = createStyle(isActiveLink, activeBorderBottom);
  return (
    <TextLinkComponent key={index} href={href} linkStyle={linkStyle}>
      <Span styles={semanticCSS(contentStyle, style.link)}>{text}</Span>
    </TextLinkComponent>
  );
};

export const AMPPrimaryLinksComponent: React.FunctionComponent<PrimaryLinksComponentProps> = props => {
  const { links, idleColor, fontSizesStyles, activeBorderBottom, hoverStateColor, activeStateColor, pagePath } = props;
  return (
    <React.Fragment>
      {links.map(createLink(idleColor, activeBorderBottom, hoverStateColor, activeStateColor, pagePath, fontSizesStyles))}
    </React.Fragment>
  );
};

const componentConfigThemeKey = 'primaryLinks';

const mapThemeProps = (theme: PrimaryLinksThemeProps) => {
  return {
    idleColor: theme.idleColor,
    activeStateColor: theme.activeStateColor,
    hoverStateColor: theme.hoverStateColor,
    activeBorderBottom: theme.activeBorderBottom,
    hoverOtherLinksColors: theme.hoverOtherLinksColors,
    baseUnit: theme.baseUnit,
    fontSizesStyles: theme.fontSizesStyles,
  };
};

export const AMPPrimaryLinks = (props: PrimaryLinksDataProps) => {
  const themeContext = useTheme(mapThemeProps, componentConfigThemeKey);
  return <ErrorBoundary><AMPPrimaryLinksComponent {...themeContext} {...props} /></ErrorBoundary>;
};
